import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { pnlColor } from "../chart/chartColors";

const COLOR_MUTED = "hsl(var(--muted-foreground))";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function MetricTile({
  label,
  value,
  desc,
}: {
  label: string;
  value: number | null;
  desc: string;
}) {
  const isNa = value === null;
  const display = isNa ? "—" : value.toFixed(2);
  const color = isNa ? COLOR_MUTED : pnlColor(value > 0 ? 1 : -1);

  return (
    <div className="rounded-lg bg-muted/40 p-3 flex flex-col gap-1.5">
      <p className="text-[10px] font-semibold tracking-widest uppercase widget-label">
        {label}
      </p>
      <p className="text-2xl font-bold font-mono" style={{ color }}>
        {display}
      </p>
      <p className="text-[10px] text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

export default function RiskAdjustedWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.riskAdjusted} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.dashboard.widgets.riskAdjusted} isDark={isDark} compact />
      <CardContent className="flex-1 flex flex-col justify-center gap-3 px-4 pb-4 pt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MetricTile
            label={t.analytics.calmarRatio}
            value={analyticsData.calmarRatio}
            desc={t.analytics.calmarRatioDesc}
          />
          <MetricTile
            label={t.analytics.recoveryFactor}
            value={analyticsData.recoveryFactor}
            desc={t.analytics.recoveryFactorDesc}
          />
        </div>
        <p className="text-center text-[11px] font-medium text-muted-foreground">
          {t.analytics.riskAdjustedReturn}
        </p>
      </CardContent>
    </Card>
  );
}
